<template>
  <div class="image-gallery-component">
    <div class="gallery-main">
      <img 
        :src="images[currentIndex].url" 
        :alt="images[currentIndex].alt"
        @click="openLightbox"
        class="gallery-current-image"
      >
      <div v-if="images[currentIndex].caption" class="gallery-caption">
        {{ images[currentIndex].caption }}
      </div>
      <div class="gallery-controls">
        <button 
          @click="prevImage" 
          :disabled="currentIndex === 0"
          class="gallery-prev"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
        </button>
        <span class="image-counter">{{ currentIndex + 1 }} / {{ images.length }}</span>
        <button 
          @click="nextImage" 
          :disabled="currentIndex === images.length - 1"
          class="gallery-next"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
        </button>
      </div>
    </div>
    
    <!-- Thumbnail strip -->
    <div v-if="images.length > 1" class="gallery-thumbnails">
      <img 
        v-for="(image, index) in images"
        :key="index"
        :src="image.url"
        :alt="image.alt"
        :class="{ active: index === currentIndex }"
        @click="currentIndex = index"
        class="gallery-thumbnail"
      >
    </div>
    
    <!-- Lightbox -->
    <Teleport to="body">
      <div v-if="lightboxOpen" class="lightbox" @click="closeLightbox">
        <img 
          :src="images[currentIndex].url" 
          :alt="images[currentIndex].alt"
          @click.stop
          class="lightbox-image"
        >
        <button class="lightbox-close" @click="closeLightbox">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
        
        <!-- Lightbox navigation -->
        <button 
          v-if="currentIndex > 0"
          @click.stop="prevImage" 
          class="lightbox-nav lightbox-prev"
        >
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
        </button>
        <button 
          v-if="currentIndex < images.length - 1"
          @click.stop="nextImage" 
          class="lightbox-nav lightbox-next"
        >
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
        </button>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'

const props = defineProps({
  images: {
    type: Array,
    required: true,
    validator: (images) => {
      return images.every(img => img.url)
    }
  }
})

const currentIndex = ref(0)
const lightboxOpen = ref(false)

const nextImage = () => {
  if (currentIndex.value < props.images.length - 1) {
    currentIndex.value++
  }
}

const prevImage = () => {
  if (currentIndex.value > 0) {
    currentIndex.value--
  }
}

const openLightbox = () => {
  lightboxOpen.value = true
  document.body.style.overflow = 'hidden'
}

const closeLightbox = () => {
  lightboxOpen.value = false
  document.body.style.overflow = ''
}

// Keyboard navigation
const handleKeyboard = (e) => {
  if (!lightboxOpen.value) return
  
  switch(e.key) {
    case 'ArrowLeft':
      prevImage()
      break
    case 'ArrowRight':
      nextImage()
      break
    case 'Escape':
      closeLightbox()
      break
  }
}

onMounted(() => {
  window.addEventListener('keydown', handleKeyboard)
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeyboard)
  document.body.style.overflow = ''
})
</script>